/**
 * One flat disc hung in the sky at the camera's own distance — `SUN.BMP`'s two
 * frames, and the phase-cut moon `night-sky.ts` puts in the second one's place.
 *
 * Held apart from `sky-dressing.ts` (which owns the group) and from
 * `night-sky.ts` (which owns the ephemeris) because **both** of them build a
 * disc, and there is exactly one rule for where a disc goes and how big it is.
 * `lessons.md`'s 2026-08-21 note is the reason it is not written twice: a
 * second implementation of a stated rule is a place for a bug to hide with a
 * good explanation attached.
 *
 * ## The size rule
 *
 * `sun.c` blits `SUN.BMP`'s frame at 1:1 pixels — 15 scanlines of 200 for the
 * sun, 17 for the moon — with no perspective scaling, so a disc subtends a
 * *fixed fraction of the viewport* however far away the sky is taken to be.
 * `three/sprite-screen-size.ts` already inverts that rule for every other
 * sprite in the port and is deferred to here rather than re-derived. The
 * consequence worth stating: at this port's 55-degree vertical field of view
 * the sun is `15/200 * 55` ≈ **4.1 degrees across**, eight times the real
 * sun's half-degree. That is the original's own choice of art — and it is also
 * what makes a lunar phase legible on the moon at all.
 *
 * Depth comes from the *view-space* component along the camera's forward axis,
 * not the euclidean range, so a disc near the edge of a wide window keeps the
 * size it has in the middle — the same distinction the bullet lane's lesson
 * records, and the reason the scale is set every frame rather than once.
 *
 * ## Plain opaque depth, and why that sentence is here
 *
 * The obvious flags for a body at infinity are `depthWrite: false,
 * depthTest: false`, and they are what the sun disc carried: under this
 * renderer's reversed-Z that pair **rasterises nothing at all** while still
 * issuing the draw call. It is why `time-of-day-design.md` §9 found the disc
 * in *zero* frames over three attempts and concluded `/fly` could not be
 * steered to the sun — it could, and there was nothing there. A disc sits at
 * `CELESTIAL_DISTANCE_M`, 18 km inside a 26 km far plane, so it has an honest
 * depth to write, and writing it is what lets a hill or an aeroplane in front
 * of it occlude it — the behaviour the flags were reaching for.
 */

import * as THREE from "three/webgpu";
import { CAMERA_FOV_DEG } from "./flight-view.ts";
import type { CelestialBody, CelestialDisc } from "./time-of-day.ts";
import { metresPerScreenPixel, metresPerScreenPixelX } from "../three/sprite-screen-size.ts";
import { CELESTIAL_DISTANCE_M } from "./world.ts";

/** After the dome (`-1`), before the world (`0`). */
export const CELESTIAL_RENDER_ORDER = -0.5;

/** A unit disc; the mesh's scale carries the size the blit rule solves for. */
export function discGeometry(segments = 48): THREE.CircleGeometry {
  return new THREE.CircleGeometry(DISC_RADIUS, segments);
}

/** The radius `discGeometry` builds, so a shader can normalise `positionGeometry` against it. */
export const DISC_RADIUS = 0.5;

/**
 * The camera's vertical field of view. `onBeforeRender` is typed against the
 * base `Camera`, and only a perspective one carries an `fov` — every camera
 * `/fly` builds is perspective (`flight-view.ts`), so the fallback is the
 * constant that camera is built with rather than a guess.
 */
export function fovYRadiansOf(camera: THREE.Camera): number {
  const degrees = camera instanceof THREE.PerspectiveCamera ? camera.fov : CAMERA_FOV_DEG;
  return (degrees * Math.PI) / 180;
}

/**
 * Puts a disc a fixed distance along `direction` from the camera, faces it
 * square to the lens, and sizes it so its frame covers the scanlines the
 * original's blit covered. Returns the camera's facing, which the moon needs
 * again to resolve its bright limb onto screen axes.
 */
export function placeCelestialDisc(
  mesh: THREE.Mesh,
  direction: { readonly x: number; readonly y: number; readonly z: number },
  disc: CelestialDisc,
  camera: THREE.Camera,
): THREE.Quaternion {
  const eye = camera.getWorldPosition(TEMP_EYE);
  const bearing = TEMP_BEARING.set(direction.x, direction.y, direction.z).normalize();
  mesh.position.copy(eye).addScaledVector(bearing, CELESTIAL_DISTANCE_M);
  const facing = camera.getWorldQuaternion(TEMP_FACING);
  mesh.quaternion.copy(facing);

  const forward = TEMP_FORWARD.set(0, 0, -1).applyQuaternion(facing);
  const depthM = bearing.dot(forward) * CELESTIAL_DISTANCE_M;
  const fovY = fovYRadiansOf(camera);
  mesh.scale.set(
    disc.widthPx * metresPerScreenPixelX(depthM, fovY),
    disc.heightPx * metresPerScreenPixel(depthM, fovY),
    1,
  );
  mesh.updateMatrixWorld();
  return facing;
}

/** `SUN.BMP`'s own flat single-colour disc — the authentic sun, or the authentic moon. */
export function discMesh(body: CelestialBody): THREE.Mesh {
  const mesh = new THREE.Mesh(discGeometry(), new THREE.MeshBasicMaterial({ color: body.disc.colour, fog: false }));
  mesh.renderOrder = CELESTIAL_RENDER_ORDER;
  // Its bounding sphere is recentred as the renderer draws it, which is after
  // the frustum test would have read the stale one — `createSky`'s hazard.
  mesh.frustumCulled = false;
  mesh.onBeforeRender = (_renderer, _scene, camera) => void placeCelestialDisc(mesh, body.direction, body.disc, camera);
  return mesh;
}

const TEMP_EYE = new THREE.Vector3();
const TEMP_BEARING = new THREE.Vector3();
const TEMP_FORWARD = new THREE.Vector3();
const TEMP_FACING = new THREE.Quaternion();
